import { useContext } from 'react'

import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import PageDataContext from '@src/quince-site-common/src/contexts/page-data-context/page-data-context'

import useDeviceInfo, { DEVICE_TYPES } from './useDeviceInfo'

const VALID_PAGE_CONFIGS = {
  PDP: {
    pageType: 'PDP',
    ssrPageType: 'product',
    templateType: 'PDP',
    trackPageType: 'Product Page',
  },
  SEARCH: {
    pageType: '',
    ssrPageType: 'page',
    templateType: 'SEARCH_PAGE',
    trackPageType: 'Search Page',
  },
  COLLECTION: {
    pageType: 'COLLECTION',
    ssrPageType: 'collection',
    templateType: 'COLLECTION',
    trackPageType: 'Collection Page',
  },
  HOME: {
    pageType: 'HOME',
    ssrPageType: 'homePage',
    templateType: 'HOME',
    trackPageType: 'Home Page',
  },
  ACCOUNT: {
    pageType: 'ACCOUNT',
    templateType: 'ACCOUNT',
    trackPageType: 'ACCOUNT',
  },
  WISHLIST: {
    pageType: null,
    ssrPageType: 'page',
    templateType: 'LANDING',
    trackPageType: 'Wishlist Page',
  },
} as const

export interface MobileSearchExperimentReturn {
  isShow1: boolean
  isShow2: boolean
  isShow3: boolean
  isControl: boolean
  newMobileMenuExperimentBucket: string | null
  isNewMobileMenuExperimentActive: boolean
  isNewMobileMenuExperimentInitialized: boolean
  isSearchPage: boolean
  isCollectionPage: boolean
}

export const useMobileSearchExperiment = (): MobileSearchExperimentReturn => {
  const { experimentBucket: newMobileMenuExperimentBucket, init: isNewMobileMenuExperimentInitialized } =
    useStatsigExperiment(STATSIG_EXPERIMENTS.MWEB_EXPOSED_SEARCH)
  const devices = useDeviceInfo()
  const isMobile = devices.deviceType === DEVICE_TYPES.MOBILE

  const pageContext = useContext(PageDataContext)

  const isSearchPage = pageContext?.templateType === VALID_PAGE_CONFIGS.SEARCH.templateType
  const isCollectionPage = pageContext?.pageType === VALID_PAGE_CONFIGS.COLLECTION.pageType

  // Determine experiment group
  const experimentGroup = isMobile ? newMobileMenuExperimentBucket : null
  const isShow1 = experimentGroup === STATSIG_EXPERIMENTS_BUCKETS.GROUP_A
  const isShow2 = experimentGroup === STATSIG_EXPERIMENTS_BUCKETS.GROUP_B
  const isShow3 = experimentGroup === STATSIG_EXPERIMENTS_BUCKETS.GROUP_C

  const shouldShowShow3 = Object.values(VALID_PAGE_CONFIGS).some(config => {
    // For WISHLIST: match by trackPageType since it's most specific
    if (config.trackPageType === 'Wishlist Page') {
      return pageContext?.trackPageType === 'Wishlist Page'
    }

    // For other pages, match by pageType if available, otherwise by templateType
    if (config.pageType && config.pageType !== null && config.pageType.length > 0) {
      return pageContext?.pageType === config.pageType
    }

    // For pages without pageType (like SEARCH), match by templateType
    return pageContext?.templateType === config.templateType
  })

  const isPDP = pageContext?.pageType === VALID_PAGE_CONFIGS.PDP.pageType
  const shouldBehaveAsShow2 = (isPDP && isShow3) || !shouldShowShow3
  const isControl = experimentGroup === STATSIG_EXPERIMENTS_BUCKETS.CONTROL

  return {
    isShow1,
    isShow2: isShow2 || shouldBehaveAsShow2,
    isShow3: isShow3 && !shouldBehaveAsShow2,
    isControl,
    newMobileMenuExperimentBucket,
    isNewMobileMenuExperimentActive: isShow1 || isShow2 || isShow3,
    isNewMobileMenuExperimentInitialized,
    isSearchPage,
    isCollectionPage,
  }
}
